import React, { useCallback, useState } from 'react';
import { useObserverReturningStore } from '../../../../CodesThatsGottaGoSomewhereElse';
import { JiraIssueData, JiraSprintLiteData } from '../../../../api/endpoints/jira';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../../concepts/objects';
import { Colors } from '../../../../ui';
import { useResolve } from '../../../Container';
import { escape } from '../../../canvas/commands/ManipulationCommands';
import { TextAlignment, TextSettings } from '../../../canvas/entities/TextConventions';
import { UIField, UIFieldProps } from '../../../canvas/entities/UIField';
import { PopperFieldEditor, PopperFieldEditorContentProps } from '../../../canvas/workspace/adapters/PopperFieldEditor';
import { SimpleTextFieldInput } from '../../../canvas/workspace/adapters/SimpleTextFieldInput';
import { Flexer } from '../../../dashboard/elements/LayoutElements';
import { jiraCardSetCustom } from '../AtlassianCommands';
import { useBinding } from '../BindingManager';
import { JiraCard, JiraCardCustomInfo } from '../JiraCard';
import { JiraSprintMenu } from '../menus/JiraSprintMenu';
import { JiraOAuthRequired } from './JiraOAuthRequired';
import { JiraOAuthRequiredPopperContent } from './JiraOAuthRequiredPopperContent';

export const JiraCustomFieldTextSettings = TextSettings.from({
    fontFamily: 'Rubik',
    fontColor: Colors.text,
    fontWeight: 400,
    fontSize: 16,
    lineHeight: 1.25,
    textMargin: 2,
    fontStyle: 'normal',
    maxFontSize: null,
    textAlignH: TextAlignment.middle,
    textAlignV: TextAlignment.middle,
});

const JiraSprintMenuFieldEditor = ({ field, onClose }: PopperFieldEditorContentProps) => {

    const dispatch = useResolve(CommandDispatcher);
    const card = field.owner.cast(JiraCard);

    const onSelect = (tm: JiraSprintLiteData) => {
        // Special hack, if the null item, set the label to empty
        if (tm.id === null) {
            tm = { id: null, name: '' };
        }

        const cf = card.customs.find(x => x.type === 'sprint');
        if (!!cf) {
            dispatch(jiraCardSetCustom, [cf.key, tm, [card.id]]);
        }
        onClose();
    };

    return <Flexer>
        <JiraSprintMenu jiraProjectId={card.project} onSelect={onSelect} includeEmpty={true} />
    </Flexer>;
};

const JiraCustomNumberFieldEditor = ({ active, field }: UIFieldProps) => {

    const objects = useResolve(ObjectManager);
    const dispatch = useResolve(CommandDispatcher);
    const object = field.owner.cast(JiraCard);
    const binding = useObserverReturningStore(useBinding<JiraIssueData>(object));
    const info = binding?.getState()?.custom?.[field.name];
    const value = ['', null, undefined].includes(info?.value) ? null : parseFloat(info?.value) || 0;

    const [initialNumber] = useState(value);

    const onChange = useCallback((nextValue: string) => {
        const nextNumber = parseFloat(nextValue) || 0;
        objects.update(u => {
            u.rolling(UIField.key(field));
            binding.mutate({ custom: { [field.name]: { value: nextNumber } } });
        });
    }, [field]);

    const onCommit = useCallback((finalValue: string) => {
        const finalNumber = parseFloat(finalValue) || 0;
        objects.update(u => {
            u.rolling(UIField.key(field));
            u.noop();
            if (finalNumber === initialNumber) return;
            dispatch(jiraCardSetCustom, [field.name, finalNumber, [object.id]]);
        });
    }, []);

    const onReject = () => dispatch(escape);

    return <JiraOAuthRequired onReject={onReject}>
        <SimpleTextFieldInput {...{
            active,
            field,
            settings: JiraCustomFieldTextSettings,
            value: value === null ? '' : `${value}`,
            onChange,
            onCommit,
        }} />
    </JiraOAuthRequired>;
};

const JiraCustomTextFieldEditor = ({ active, field }: UIFieldProps) => {

    const objects = useResolve(ObjectManager);
    const dispatch = useResolve(CommandDispatcher);
    const object = field.owner.cast(JiraCard);
    const binding = useObserverReturningStore(useBinding<JiraIssueData>(object));
    const info = binding?.getState()?.custom?.[field.name];
    const value = info?.value ?? '';

    const [initialValue] = useState(value);

    const onChange = useCallback((nextValue: string) => {
        objects.update(u => {
            u.rolling(UIField.key(field));
            binding.mutate({ custom: { [field.name]: { value: nextValue } } });
        });
    }, [field]);

    const onCommit = useCallback((finalValue: string) => {
        objects.update(u => {
            u.rolling(UIField.key(field));
            u.noop();
            if (finalValue === initialValue) return;
            dispatch(jiraCardSetCustom, [field.name, finalValue, [object.id]]);
        });
    }, []);

    const onReject = () => dispatch(escape);

    return <JiraOAuthRequired onReject={onReject}>
        <SimpleTextFieldInput {...{
            active,
            field,
            settings: JiraCustomFieldTextSettings,
            value,
            onChange,
            onCommit,
        }} />
    </JiraOAuthRequired>;
};

const typeEditorMap = {
    number: JiraCustomNumberFieldEditor,
    sprint: PopperFieldEditor(JiraOAuthRequiredPopperContent(JiraSprintMenuFieldEditor)),
    string: JiraCustomTextFieldEditor,
};

export const JiraCustomField = (owner: JiraCard, info: JiraCardCustomInfo): UIField => ({
    ui: typeEditorMap[info.type] ?? JiraCustomTextFieldEditor,
    name: info.key,
    bounds: owner.layout.customs[info.key],
    owner,
});
